import { Component } from '@theme/component';

/**
 * Selector cascade used to find the product gallery to scroll to.
 *
 * Ordered from the most explicit to the loosest. The first two entries are the ids Dawn prints on
 * its gallery wrappers (kept so the section keeps working on the old theme and on any page that
 * still renders Dawn markup). The next entries are Horizon's own product gallery:
 * `assets/media-gallery.js` + the `.media-gallery--carousel` wrapper in `assets/base.css`, the
 * `_product-media-gallery` block rendered by `sections/product-information.liquid`, and the
 * `.product-media` / `.product-media-container` wrappers. The last two are Dawn's outer wrappers.
 *
 * @type {string[]}
 */
const GALLERY_SELECTORS = [
  '[id^="MediaGallery-"]',
  '[id^="GalleryViewer-"]',
  'media-gallery',
  '.media-gallery',
  'product-media-gallery',
  '.product-media-gallery',
  '.product-media-container',
  '.product-media',
  '.product__media-wrapper',
  '.product__info-wrapper',
];

/**
 * Resolves the element the "Check Availability" call to action should scroll to.
 *
 * Tries the merchant-provided selector first, then the cascade above. Every lookup is guarded, so
 * a malformed selector degrades to the next candidate instead of throwing.
 *
 * @param {string} custom - Merchant selector coming from the `scroll_target` setting.
 * @returns {Element | null}
 */
export function resolveGalleryTarget(custom) {
  const selectors = custom ? [custom, ...GALLERY_SELECTORS] : GALLERY_SELECTORS;

  for (const selector of selectors) {
    try {
      const element = document.querySelector(selector);

      if (element) return element;
    } catch (error) {
      /* invalid selector — fall through to the next candidate */
    }
  }

  return null;
}

/**
 * Scrolls to `target`, honouring `prefers-reduced-motion`.
 *
 * @param {Element} target
 */
export function scrollToTarget(target) {
  const reduced =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
}

/**
 * PDP hero.
 *
 * Static markup apart from one behaviour: the call to action scrolls to the product gallery
 * instead of navigating. If no gallery can be found the click is left alone and the anchor keeps
 * its default behaviour (it points at the product URL), which is the whole point of the cascade.
 *
 * @extends {Component}
 */
export class OcPdpHeroComponent extends Component {
  /**
   * @param {Event} event - The click on the call to action.
   */
  scrollToGallery(event) {
    try {
      const target = resolveGalleryTarget((this.dataset.scrollTarget || '').trim());

      if (!target) return;

      event.preventDefault();

      scrollToTarget(target);
    } catch (error) {
      /* never break the page — the anchor keeps its default behaviour */
    }
  }
}

if (!customElements.get('oc-pdp-hero-component')) {
  customElements.define('oc-pdp-hero-component', OcPdpHeroComponent);
}
